import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Combobox,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { MarketingEmailAccount, MarketingSettings, MarketingSettingsInput } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Marketing → Settings → General. `GET` / `PATCH /marketing/settings`, a
 * singleton.
 *
 * ⚠️ Save sends ONLY the keys that moved — the PATCH is `.strict()` and an
 * empty body is a 422, so Save is disabled while nothing has changed.
 *
 * ⚠️ `null` is a real answer on both numeric settings and never `0`:
 * `frequencyCapPerWeek: null` is NO cap, `approvalThreshold: null` is NEVER
 * needs approval. Each is a checkbox plus a number so neither absence can be
 * typed as a zero.
 *
 * The page gates the READ; this card gates the WRITE on
 * `ability.canUpdate('marketing-settings')`.
 */

const MODULE = 'marketing-settings' as const;

const KNOWN_KEYS = [
  'postalAddress',
  'footerExtraText',
  'frequencyCapPerWeek',
  'approvalThreshold',
  'defaultEmailAccountId',
  'trackOpens',
  'trackClicks',
] as const;

interface Draft {
  postalAddress: string;
  footerExtraText: string;
  capOn: boolean;
  cap: string;
  approvalOn: boolean;
  threshold: string;
  defaultEmailAccountId: string;
  trackOpens: boolean;
  trackClicks: boolean;
}

function draftOf(s: MarketingSettings): Draft {
  return {
    postalAddress: s.postalAddress ?? '',
    footerExtraText: s.footerExtraText ?? '',
    capOn: s.frequencyCapPerWeek !== null,
    cap: s.frequencyCapPerWeek === null ? '3' : String(s.frequencyCapPerWeek),
    approvalOn: s.approvalThreshold !== null,
    threshold: s.approvalThreshold === null ? '200' : String(s.approvalThreshold),
    defaultEmailAccountId: s.defaultEmailAccountId === null ? '' : String(s.defaultEmailAccountId),
    trackOpens: s.trackOpens,
    trackClicks: s.trackClicks,
  };
}

const textOrNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

function parseCount(raw: string, max: number): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= max ? n : null;
}

/** Builds the PATCH from the keys that moved, plus local refusals. */
function buildPatch(
  s: MarketingSettings,
  d: Draft,
): { patch: MarketingSettingsInput; errors: Record<string, string> } {
  const patch: MarketingSettingsInput = {};
  const errors: Record<string, string> = {};

  const postal = textOrNull(d.postalAddress);
  if (postal !== s.postalAddress) patch.postalAddress = postal;
  const extra = textOrNull(d.footerExtraText);
  if (extra !== s.footerExtraText) patch.footerExtraText = extra;

  if (d.capOn) {
    const cap = parseCount(d.cap, 65535);
    if (cap === null) errors.frequencyCapPerWeek = 'Enter a whole number of at least 1.';
    else if (cap !== s.frequencyCapPerWeek) patch.frequencyCapPerWeek = cap;
  } else if (s.frequencyCapPerWeek !== null) {
    patch.frequencyCapPerWeek = null;
  }

  if (d.approvalOn) {
    const threshold = parseCount(d.threshold, 4294967295);
    if (threshold === null) errors.approvalThreshold = 'Enter a whole number of at least 1.';
    else if (threshold !== s.approvalThreshold) patch.approvalThreshold = threshold;
  } else if (s.approvalThreshold !== null) {
    patch.approvalThreshold = null;
  }

  const account = d.defaultEmailAccountId === '' ? null : Number(d.defaultEmailAccountId);
  if (account !== s.defaultEmailAccountId) patch.defaultEmailAccountId = account;

  if (d.trackOpens !== s.trackOpens) patch.trackOpens = d.trackOpens;
  if (d.trackClicks !== s.trackClicks) patch.trackClicks = d.trackClicks;

  return { patch, errors };
}

/** The locked footer as every marketing email will carry it — plain markup. */
function FooterSample({ postalAddress, extra }: { postalAddress: string; extra: string }) {
  const postal = postalAddress.trim();
  const extraText = extra.trim();
  return (
    // content-start: the figure is stretched to the form column's height, and a
    // stretched grid spread its two rows apart — the sample sat far below its caption.
    <figure className="m-0 grid content-start gap-2">
      <figcaption className="text-xs font-medium text-fg-secondary">Compliance footer sample</figcaption>
      <div className="rounded-md border border-dashed border-hairline-strong bg-surface-sunken px-4 py-3 text-center text-xs text-fg-muted">
        {extraText !== '' && <p className="m-0 mb-2 whitespace-pre-line [overflow-wrap:anywhere]">{extraText}</p>}
        {postal !== '' ? (
          <p className="m-0 whitespace-pre-line [overflow-wrap:anywhere]">{postal}</p>
        ) : (
          <p className="m-0 font-medium text-danger">No postal address — campaigns cannot be scheduled until one is saved.</p>
        )}
        <p className="m-0 mt-2">
          <span className="underline">Unsubscribe</span> from these emails
        </p>
      </div>
    </figure>
  );
}

export function GeneralSettingsCard() {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate(MODULE);
  const canReadAccounts = ability.canRead('marketing-email-accounts');

  const [settings, setSettings] = useState<MarketingSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<MarketingEmailAccount[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveRefused, setSaveRefused] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [showLocalErrors, setShowLocalErrors] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getMarketingSettings(controller.signal)
      .then((response) => {
        setSettings(response.data);
        setDraft(draftOf(response.data));
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(caught.message || 'Your account is not allowed to read marketing settings.');
          return;
        }
        setLoadError(caught instanceof Error ? caught.message : 'Marketing settings could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!canReadAccounts) return;
    const controller = new AbortController();
    api
      .listMarketingEmailAccounts(controller.signal)
      .then((response) => setAccounts(response.data))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setAccountsError(caught instanceof Error ? caught.message : 'Email accounts could not be loaded.');
      });
    return () => controller.abort();
  }, [canReadAccounts]);

  const built = useMemo(
    () => (settings && draft ? buildPatch(settings, draft) : { patch: {}, errors: {} }),
    [settings, draft],
  );
  const dirty = Object.keys(built.patch).length > 0 || Object.keys(built.errors).length > 0;

  const accountOptions = useMemo(() => {
    const options = (accounts ?? []).map((a) => ({
      value: String(a.id),
      label: a.name,
      hint: a.isActive ? a.fromEmail : `${a.fromEmail} · inactive`,
    }));
    const stored = settings?.defaultEmailAccountId ?? null;
    if (stored !== null && !options.some((o) => o.value === String(stored))) {
      options.unshift({
        value: String(stored),
        label: accounts === null ? `Account #${stored}` : `Account #${stored} (no longer listed)`,
        hint: '',
      });
    }
    return options;
  }, [accounts, settings]);

  function update(next: Partial<Draft>) {
    setDraft((d) => (d ? { ...d, ...next } : d));
    setSaved(false);
    setSaveError(null);
  }

  async function save() {
    if (!settings || !draft) return;
    if (Object.keys(built.errors).length > 0) {
      setShowLocalErrors(true);
      return;
    }
    if (Object.keys(built.patch).length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaveRefused(null);
    setServerErrors({});
    try {
      const response = await api.updateMarketingSettings(built.patch);
      setSettings(response.data);
      setDraft(draftOf(response.data));
      setShowLocalErrors(false);
      setSaved(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setSaveRefused(caught.message || 'Your account is not allowed to change marketing settings.');
      } else if (caught instanceof ApiError) {
        const { fields, unroutable } = caught.routeFieldErrors(KNOWN_KEYS);
        setServerErrors(fields);
        setSaveError(unroutable.length > 0 ? unroutable.join(' ') : Object.keys(fields).length > 0 ? null : caught.message);
      } else {
        setSaveError('Could not reach the server. Nothing was saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  const fieldError = (key: (typeof KNOWN_KEYS)[number]) =>
    serverErrors[key] ?? (showLocalErrors ? built.errors[key] : undefined);

  return (
    <Panel className="min-w-0" aria-labelledby="marketing-general-heading">
      <PanelHeader
        id="marketing-general-heading"
        title="General"
        description={
          settings?.updatedAt
            ? `Last changed ${formatDateTime(settings.updatedAt)}${settings.updatedBy ? ` by ${settings.updatedBy.name || settings.updatedBy.email}` : ''}`
            : undefined
        }
      />

      {forbidden ? (
        <PanelBody>
          <Notice tone="danger" role="alert" title="Your account is not allowed to read marketing settings.">
            <p className="m-0 [overflow-wrap:anywhere]">{forbidden}</p>
          </Notice>
        </PanelBody>
      ) : loadError ? (
        <PanelBody>
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load marketing settings.</>}>
            <p className="m-0 [overflow-wrap:anywhere]">{loadError}</p>
          </Notice>
        </PanelBody>
      ) : loading || !draft ? (
        <PanelBody>
          <div className="grid gap-4" aria-busy="true">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        </PanelBody>
      ) : (
        <>
          <PanelBody>
            <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
              <div className="grid min-w-0 content-start gap-4">
                <Field label="Postal address" required error={fieldError('postalAddress')}>
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={3}
                      value={draft.postalAddress}
                      onChange={(e) => update({ postalAddress: e.target.value })}
                      disabled={!canUpdate || saving}
                      invalid={Boolean(fieldError('postalAddress'))}
                    />
                  )}
                </Field>

                <Field label="Footer extra text" error={fieldError('footerExtraText')}>
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={2}
                      value={draft.footerExtraText}
                      onChange={(e) => update({ footerExtraText: e.target.value })}
                      disabled={!canUpdate || saving}
                      invalid={Boolean(fieldError('footerExtraText'))}
                    />
                  )}
                </Field>

                <fieldset className="m-0 grid gap-2 border-0 p-0">
                  <legend className="mb-1 text-xs font-medium text-fg-secondary">Frequency cap</legend>
                  <Checkbox
                    label="Limit marketing emails per address"
                    checked={draft.capOn}
                    onChange={(e) => update({ capOn: e.target.checked })}
                    disabled={!canUpdate || saving}
                  />
                  {draft.capOn && (
                    <div className="max-w-56">
                      <Field label="Emails per rolling 7 days" error={fieldError('frequencyCapPerWeek')}>
                        {(props) => (
                          <Input
                            {...props}
                            inputMode="numeric"
                            value={draft.cap}
                            onChange={(e) => update({ cap: e.target.value })}
                            disabled={!canUpdate || saving}
                            invalid={Boolean(fieldError('frequencyCapPerWeek'))}
                          />
                        )}
                      </Field>
                    </div>
                  )}
                </fieldset>

                <fieldset className="m-0 grid gap-2 border-0 p-0">
                  <legend className="mb-1 text-xs font-medium text-fg-secondary">Approval</legend>
                  <Checkbox
                    label="Require approval for large campaigns"
                    checked={draft.approvalOn}
                    onChange={(e) => update({ approvalOn: e.target.checked })}
                    disabled={!canUpdate || saving}
                  />
                  {draft.approvalOn && (
                    <div className="max-w-56">
                      <Field label="Above this many recipients" error={fieldError('approvalThreshold')}>
                        {(props) => (
                          <Input
                            {...props}
                            inputMode="numeric"
                            value={draft.threshold}
                            onChange={(e) => update({ threshold: e.target.value })}
                            disabled={!canUpdate || saving}
                            invalid={Boolean(fieldError('approvalThreshold'))}
                          />
                        )}
                      </Field>
                    </div>
                  )}
                </fieldset>

                <Field
                  label="Default email account"
                  error={
                    fieldError('defaultEmailAccountId') ??
                    (accountsError ? `Email accounts could not be loaded: ${accountsError}` : undefined)
                  }
                >
                  {(props) => (
                    <Combobox
                      id={props.id}
                      value={draft.defaultEmailAccountId}
                      onChange={(value) => update({ defaultEmailAccountId: value })}
                      options={accountOptions}
                      emptyOptionLabel="No default"
                      placeholder="No default"
                      loading={canReadAccounts && accounts === null && accountsError === null}
                      disabled={!canUpdate || saving || !canReadAccounts || accountsError !== null}
                      invalid={Boolean(fieldError('defaultEmailAccountId'))}
                    />
                  )}
                </Field>

                <fieldset className="m-0 grid gap-2 border-0 p-0">
                  <legend className="mb-1 text-xs font-medium text-fg-secondary">Tracking</legend>
                  <Checkbox
                    label="Track opens (an estimate)"
                    checked={draft.trackOpens}
                    onChange={(e) => update({ trackOpens: e.target.checked })}
                    disabled={!canUpdate || saving}
                  />
                  <Checkbox
                    label="Track clicks"
                    checked={draft.trackClicks}
                    onChange={(e) => update({ trackClicks: e.target.checked })}
                    disabled={!canUpdate || saving}
                  />
                </fieldset>
              </div>

              <FooterSample postalAddress={draft.postalAddress} extra={draft.footerExtraText} />
            </div>
          </PanelBody>

          <PanelFooter>
            <div className="grid min-w-0 gap-1 text-xs">
              {saveRefused && (
                <p role="alert" className="m-0 text-danger [overflow-wrap:anywhere]">
                  {saveRefused}
                </p>
              )}
              {saveError && (
                <p role="alert" className="m-0 text-danger [overflow-wrap:anywhere]">
                  {saveError}
                </p>
              )}
              {saved && !dirty && (
                <p role="status" className="m-0 text-success">
                  Saved.
                </p>
              )}
              {!canUpdate && (
                <p className="m-0 text-fg-muted">Your permission set does not include changing these settings.</p>
              )}
            </div>
            {canUpdate && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={!dirty || saving}
                  onClick={() => {
                    if (settings) setDraft(draftOf(settings));
                    setShowLocalErrors(false);
                    setServerErrors({});
                    setSaveError(null);
                  }}
                >
                  Discard changes
                </Button>
                <Button variant="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
                  Save settings
                </Button>
              </div>
            )}
          </PanelFooter>
        </>
      )}
    </Panel>
  );
}
